<template>
  <div class="divide-y divide-gray-200">
    <div class="px-4 py-3 sm:px-6 sm:py-4">
      <MetricChartQueryBuilder @query="loadData" />
    </div>
    <div v-if="loading" class="p-12 text-center">
      <Loader />
    </div>
    <template v-else-if="insights && insights.length">
      <div class="divide-y divide-gray-200">
        <div class="px-4 py-3 sm:px-6 sm:py-4">
          <h3 class="text-lg font-medium">
            {{ metric.title }}
          </h3>
          <p class="text-gray-600 text-sm">
            {{ metric.description }}
          </p>
        </div>
        <div class="px-4 py-3 sm:px-6 sm:py-4">
          <AreaChart :values="chartData" :metric="metric" />
          <template v-if="dateRange.length === 2">
            <h4 class="text-base font-medium text-center mt-10 mb-4">
              Facebook Campaigns Active By Day
            </h4>
            <TimelineChart :data="facebookCampaignInsights" :range="dateRange" />
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import { first, last } from 'lodash-es';
import { accountFacebookCampaignReportsClient, accountInsightsClient } from '@/api/clients';
import convertReportDataToTimelineChartData from '@/utils/convert-report-data-to-timeline-chart-data';
import MetricChartQueryBuilder from './metric-chart-query-builder.vue';
import AreaChart from './area-chart.vue';
import TimelineChart from './timeline-chart.vue';

export default {
  components: {
    MetricChartQueryBuilder,
    AreaChart,
    TimelineChart,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
    metric: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      insights: null,
      facebookCampaignInsights: null,
      rejectReason: null,
      query: null,
    };
  },
  computed: {
    chartData() {
      return (this.insights || []).map(({ date, value }) => ({
        date: moment(date).format('YYYY-MM-DD'),
        value,
      }));
    },
    dateRange() {
      if (this.chartData.length === 0) {
        return [];
      }

      const since = first(this.chartData).date;
      const until = last(this.chartData).date;

      return [since, until];
    },
  },
  methods: {
    async loadData(query) {
      this.query = query;
      try {
        this.loading = true;

        if (this.query.since) {
          await Promise.all([this.loadInsights()]);
        }
        await this.loadInsights();
        await this.loadFacebookCampaignInsights();
      } catch (error) {
        this.rejectReason = error.message;
      } finally {
        this.loading = false;
      }
    },
    async loadInsights() {
      try {
        const response = await accountInsightsClient.read(this.accountId, {
          metric: [this.metric.name],
          ...this.query,
        });

        this.insights = response?.data?.[`${this.metric.name}`] || null;

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async loadFacebookCampaignInsights() {
      const dateBetween = this.dateRange.join(',');

      if (!dateBetween) {
        return Promise.resolve();
      }

      try {
        const response = await accountFacebookCampaignReportsClient.list(this.accountId, {
          filter: {
            date_between: dateBetween,
          },
          fields: {
            facebook_reports: 'id,date_from,facebook_campaign_id',
          },
        });

        this.facebookCampaignInsights = convertReportDataToTimelineChartData(response.data);

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
  },
};
</script>
